import { useEffect, useState } from "react";
import API from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Bank, Copy } from "@phosphor-icons/react";
import { CardShell, fmtWhen } from "./shared";

export default function GoCardlessCard({ item, isAdmin, onChange }) {
  const conn = item?.connection;
  const extra = item?.extra || {};
  const [busy, setBusy] = useState("");
  const [form, setForm] = useState({ access_token: "", environment: conn?.account?.environment || "sandbox", webhook_secret: "" });
  const [settings, setSettings] = useState(conn?.settings || {});
  const [editing, setEditing] = useState(!conn);

  useEffect(() => { setSettings(conn?.settings || {}); setEditing(!conn); setForm((f) => ({ ...f, environment: conn?.account?.environment || f.environment })); }, [conn]);

  const connect = async () => {
    setBusy("connect");
    try {
      await API.post("/integrations/gocardless/connect", form);
      toast.success(conn ? "GoCardless updated" : "GoCardless connected");
      setForm((f) => ({ ...f, access_token: "", webhook_secret: "" }));
      onChange();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not connect GoCardless"); }
    finally { setBusy(""); }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect GoCardless? Existing mandates stay valid at GoCardless, but nothing will be collected from Fixlr.")) return;
    setBusy("disconnect");
    try { await API.post("/integrations/gocardless/disconnect"); toast.success("GoCardless disconnected"); onChange(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Disconnect failed"); }
    finally { setBusy(""); }
  };

  const save = async (patch) => {
    setSettings((s) => ({ ...s, ...patch }));
    try { await API.put("/integrations/gocardless/settings", patch); toast.success("Direct Debit settings saved"); onChange(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const test = async () => {
    setBusy("test");
    try { const { data } = await API.post("/integrations/gocardless/test"); toast.success(`Connected: ${data.name} (${data.verification_status || "ok"})`); onChange(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Test failed"); }
    finally { setBusy(""); }
  };

  const copy = async () => {
    try { await navigator.clipboard.writeText(extra.webhook_url); toast.success("Webhook URL copied"); } catch { window.prompt("Copy:", extra.webhook_url); }
  };

  return (
    <CardShell title="GoCardless Direct Debit" icon={<Bank size={20} weight="duotone" className="text-indigo-600" />} item={item} testid="integration-gocardless"
      right={conn && <span className="text-[10px] uppercase tracking-wide text-muted-foreground" data-testid="gocardless-env">{conn.account?.environment}</span>}>
      {conn && !editing && (
        <div className="space-y-3">
          <div className="text-xs text-muted-foreground flex flex-wrap gap-x-4 gap-y-1">
            <span data-testid="gocardless-account-name">Account: <b className="text-foreground">{conn.account?.name}</b></span>
            {conn.account?.verification_status && <span>Verification: {conn.account.verification_status.replace(/_/g, " ")}</span>}
            <span>Connected {fmtWhen(conn.connected_at)}</span>
          </div>
          {isAdmin && (
            <>
              <div className="grid grid-cols-1 gap-2">
                {[["auto_collect_contracts", "Auto-collect contract invoices when sent"], ["auto_collect_all", "Auto-collect every invoice for clients with a mandate"], ["charge_on_due_date", "Charge on the invoice due date (otherwise as soon as possible)"]].map(([k, label]) => (
                  <label key={k} className="flex items-center justify-between gap-2 text-xs rounded border border-border px-2 py-1.5">
                    <span>{label}</span>
                    <Switch checked={settings[k] !== undefined ? !!settings[k] : k !== "auto_collect_all"} onCheckedChange={(v) => save({ [k]: v })} data-testid={`gocardless-toggle-${k}`} />
                  </label>
                ))}
              </div>
              <div className="rounded-md bg-muted/40 p-2.5 space-y-1">
                <p className="text-[11px] font-medium">Webhook (recommended — confirms payments the moment GoCardless does)</p>
                <div className="flex items-center gap-2">
                  <code className="text-[10px] break-all flex-1" data-testid="gocardless-webhook-url">{extra.webhook_url}</code>
                  <Button size="icon" variant="ghost" className="h-6 w-6" onClick={copy} data-testid="copy-gocardless-webhook-btn"><Copy size={12} /></Button>
                </div>
                <p className="text-[10px] text-muted-foreground">Dashboard → Developers → Create → Webhook endpoint → paste this URL, then save the secret here. {extra.has_webhook_secret ? "✓ Secret saved." : "No secret saved yet."}</p>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button size="sm" variant="outline" onClick={test} disabled={!!busy} data-testid="gocardless-test-btn">Test connection</Button>
                <Button size="sm" variant="ghost" onClick={() => setEditing(true)} data-testid="gocardless-edit-btn">Change token / secret</Button>
                <Button size="sm" variant="ghost" className="text-red-600 ml-auto" onClick={disconnect} disabled={!!busy} data-testid="gocardless-disconnect-btn">Disconnect</Button>
              </div>
            </>
          )}
        </div>
      )}
      {isAdmin && editing && (
        <div className="space-y-3" data-testid="gocardless-form">
          <div className="grid gap-3 sm:grid-cols-[1fr_140px]">
            <div>
              <Label className="text-xs">Access token</Label>
              <Input type="password" autoComplete="off" value={form.access_token} onChange={(e) => setForm({ ...form, access_token: e.target.value })}
                placeholder={extra.has_token ? "•••••••• (saved — paste to replace)" : "sandbox_… or live_…"} className="mt-1 font-mono text-xs" data-testid="gocardless-token-input" />
              <p className="text-[10px] text-muted-foreground mt-1">GoCardless Dashboard → Developers → Create → Access token (read-write). Stored encrypted.</p>
            </div>
            <div>
              <Label className="text-xs">Environment</Label>
              <Select value={form.environment} onValueChange={(v) => setForm({ ...form, environment: v })}>
                <SelectTrigger className="mt-1 h-9 text-xs" data-testid="gocardless-env-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="sandbox">Sandbox (test)</SelectItem><SelectItem value="live">Live</SelectItem></SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-xs">Webhook secret (optional)</Label>
            <Input type="password" autoComplete="off" value={form.webhook_secret} onChange={(e) => setForm({ ...form, webhook_secret: e.target.value })}
              placeholder={extra.has_webhook_secret ? "•••••••• (saved — paste to replace)" : "From your GoCardless webhook endpoint"} className="mt-1 font-mono text-xs" data-testid="gocardless-webhook-secret-input" />
          </div>
          <div className="flex gap-2">
            <Button size="sm" onClick={connect} disabled={busy === "connect" || (!form.access_token && !extra.has_token)} data-testid="gocardless-connect-btn">
              {busy === "connect" ? "Checking with GoCardless…" : conn ? "Save" : "Connect GoCardless"}
            </Button>
            {conn && <Button size="sm" variant="ghost" onClick={() => setEditing(false)} data-testid="gocardless-cancel-edit-btn">Cancel</Button>}
          </div>
        </div>
      )}
      {!isAdmin && !conn && <p className="text-xs text-muted-foreground">Ask an admin to connect GoCardless.</p>}
    </CardShell>
  );
}
